import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { calcProblem, idvFromPrice, initialCalc, masterCalc, masterIdvOf, recalc, rupees } from "../utils/idvByHand";

const BLUE = "#1976d2";
const digits = (v) => String(v || "").replace(/[^0-9.]/g, "");

// index.scss forces `border-color: transparent !important` on every outlined input; restated so each box reads as a
// box (the Make & Model master's IDV Calculator does the same).
const boxSx = (center = false) => ({
  minWidth: 0,
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD !important", borderWidth: "1px !important" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${BLUE} !important` },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: `${BLUE} !important`, borderWidth: "2px !important" },
  "& .MuiOutlinedInput-input": { fontSize: 14, fontWeight: 600, color: "#101828", textAlign: center ? "center" : "left", padding: "8px 6px" },
  "& .MuiInputAdornment-root .MuiTypography-root": { fontSize: 13, color: "#667085" },
});

const Card = ({ label, caption, highlight, children, testId }) => (
  <Box data-testid={testId} sx={{ p: 1.5, height: "100%", borderRadius: "8px", backgroundColor: highlight ? "#F5FAFF" : "#F8F9FA", border: highlight ? `2px solid ${BLUE}` : "1px solid #EAECF0" }}>
    <Typography sx={{ fontSize: 12, color: highlight ? BLUE : "#667085", fontWeight: highlight ? 700 : 600, mb: 0.75 }}>{label}</Typography>
    {children}
    {caption ? <Typography sx={{ fontSize: 11, color: "#98A2B3", mt: 0.5 }}>{caption}</Typography> : null}
  </Box>
);

/**
 * The Online Policy form's IDV Calculator (the owner, 2026-10-27): the selling price and the vehicle's depreciation give
 * the IDV, which can be typed over - but only inside the Make & Model's IDV range, which is shown and never edited
 * (utils/idvByHand.js). Update puts the price and the IDV on THIS policy; the Make & Model master is not changed.
 *
 * @param {object}   record   the chosen Make & Model row (may be null - then the figures start empty)
 * @param {object}   values   the form's current idv / manufacturerSellingPrice
 * @param {object}   range    the IDV range in force ({from, to})
 * @param {Function} onApply  (calculator state) => void
 */
const PolicyIdvCalculatorDialog = ({ open, onClose, record, values, range, onApply }) => {
  const [calc, setCalc] = useState(() => initialCalc({ record, values, range }));

  // Fresh figures each time it opens (the form may have changed meanwhile).
  useEffect(() => {
    if (open) setCalc(initialCalc({ record, values, range }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const master = masterIdvOf(record);
  const problem = calcProblem(calc);
  const fromPrice = idvFromPrice(calc.price, calc.dep);
  const hasRange = !!(calc.from || calc.to);
  const vehicle = record ? [record.make, record.model, record.vehicleVariant].filter(Boolean).join(" ") : "";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: undefined } } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 2, borderBottom: "1px solid #EAECF0", color: BLUE, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          IDV Calculator
          <Typography sx={{ fontSize: 13, color: "#667085", fontWeight: 400 }}>
            {vehicle ? `${vehicle} · ` : ""}selling price to IDV
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }} data-testid="policy-idv-calculator">
        <Typography sx={{ fontSize: 12, color: "#344054", fontWeight: 600, mb: 1 }}>Ex-Showroom / Manufacturer's Selling Price</Typography>
        <TextField
          fullWidth
          size="small"
          value={calc.price}
          placeholder="Type the selling price"
          onChange={(e) => setCalc((s) => recalc(s, { price: digits(e.target.value) }))}
          inputProps={{ "data-testid": "calc-price", inputMode: "numeric" }}
          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          sx={boxSx()}
        />
        <Typography sx={{ fontSize: 11.5, color: "#667085", mt: 0.5 }}>
          Make &amp; Model master price: {master.price ? rupees(master.price) : "not set"}
        </Typography>

        <Grid container spacing={1.5} alignItems="stretch" sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Card label="Depreciation" caption={fromPrice ? `IDV from the price: ${rupees(fromPrice)}` : "Price less this %"}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Typography sx={{ fontSize: 13, color: "#667085", whiteSpace: "nowrap", flexShrink: 0 }}>Price −</Typography>
                <TextField
                  size="small"
                  value={calc.dep}
                  onChange={(e) => setCalc((s) => recalc(s, { dep: digits(e.target.value) }))}
                  inputProps={{ "data-testid": "calc-dep", inputMode: "decimal" }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ ...boxSx(true), flex: 1 }}
                />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card label="Allowed IDV range" caption="From the Make & Model — cannot be changed here" testId="calc-range">
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#101828", py: 0.6 }}>
                {hasRange ? `${rupees(calc.from)} – ${rupees(calc.to)}` : "Not set on the Make & Model"}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card label="IDV for this policy" caption="Type over it — only inside the range" highlight>
              <TextField
                size="small"
                fullWidth
                value={calc.idv}
                onChange={(e) => setCalc((s) => ({ ...s, idv: digits(e.target.value) }))}
                inputProps={{ "data-testid": "calc-idv", inputMode: "numeric" }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={boxSx()}
              />
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          <Chip size="small" label="This policy only" sx={{ height: 18, fontSize: 10, backgroundColor: "#ECFDF3", color: "#027A48" }} />
          <Typography sx={{ fontSize: 11.5, color: "#667085" }}>
            Update puts the selling price and the IDV on this policy. The Make &amp; Model master and its range are not changed.
          </Typography>
        </Box>
        {problem ? (
          <Typography data-testid="calc-problem" sx={{ fontSize: 12.5, color: "#B42318", fontWeight: 600, mt: 1 }}>
            {problem}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #EAECF0", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Button onClick={() => setCalc(masterCalc(record, range))} disabled={!record} sx={{ textTransform: "none" }} data-testid="calc-use-master">
          Use master values
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: "none", color: "#475467" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!!problem}
            onClick={() => onApply(calc)}
            data-testid="calc-update"
            sx={{ textTransform: "none", borderRadius: 1, px: 2.5, backgroundColor: BLUE, "&:hover": { backgroundColor: "#1565c0" } }}
          >
            Update
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PolicyIdvCalculatorDialog;
